// ThreadFiltersCard — shared CRUD card for `allowed_threads` (ADR-0009)
// and `excluded_threads` (N9). Same shape, different table + tone +
// auxiliary column ("label" for allowed, "reason" for excluded).
//
// RLS: owner SELECT/INSERT/UPDATE/DELETE on both tables (migration 0005).
// The bridge daemon + webhook receivers consult these at INGEST — this UI
// is the ONLY way to manage them in production; surfacing it makes the
// Phase 1B handover possible.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, X } from 'lucide-react';
import GlassCard from '@/components/shared/GlassCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export type ThreadChannel = 'imessage' | 'sms' | 'whatsapp' | 'call' | 'email';

const CHANNELS: { value: ThreadChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'imessage', label: 'iMessage' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'call', label: 'Call' },
];

type Variant = 'allowed' | 'excluded';

type ThreadRow = {
  id: string;
  channel: ThreadChannel;
  identifier: string;
  /** Friendly label (allowed_threads) or reason text (excluded_threads). */
  meta: string | null;
  created_at: string;
};

type ThreadFiltersCardProps = {
  variant: Variant;
};

function tableNameFor(variant: Variant): 'allowed_threads' | 'excluded_threads' {
  return variant === 'allowed' ? 'allowed_threads' : 'excluded_threads';
}

function metaColumnFor(variant: Variant): 'label' | 'reason' {
  return variant === 'allowed' ? 'label' : 'reason';
}

function metaLabelFor(variant: Variant): string {
  return variant === 'allowed' ? 'Label (optional)' : 'Reason (optional)';
}

function emptyStateFor(variant: Variant): { title: string; body: string } {
  return variant === 'allowed'
    ? {
        title: 'No allowed threads yet.',
        body: 'With ALLOWLIST_MODE=true (Phase 1A), the bridge ignores any channel row not in this list. Add your test contacts here.',
      }
    : {
        title: 'Nothing excluded.',
        body: 'Any contact added here gets dropped at ingest — their messages never enter Cognitive Memory. The filter runs BEFORE storage; once added, no agent ever sees them.',
      };
}

/** Cap on the visible list. Realistic Joel use is <50 entries; the
 *  limit guards against an upstream bug ever writing thousands.
 *  Per CLAUDE.md §19 standard-review scaling-fixture rule: any SELECT
 *  without an explicit LIMIT is a scaling risk. 500 is well above the
 *  expected ceiling and well below "too many to render in a list."
 */
const THREADS_LIST_LIMIT = 500;

async function fetchThreads(variant: Variant): Promise<ThreadRow[]> {
  const table = tableNameFor(variant);
  const metaCol = metaColumnFor(variant);
  const { data, error } = await supabase
    .from(table)
    .select(`id, channel, identifier, ${metaCol}, created_at`)
    .order('created_at', { ascending: false })
    .limit(THREADS_LIST_LIMIT);
  if (error) throw error;
  return (data ?? []).map(
    (row: {
      id: string;
      channel: ThreadChannel;
      identifier: string;
      created_at: string;
      label?: string | null;
      reason?: string | null;
    }) => ({
      id: row.id,
      channel: row.channel,
      identifier: row.identifier,
      meta: variant === 'allowed' ? (row.label ?? null) : (row.reason ?? null),
      created_at: row.created_at,
    }),
  );
}

export default function ThreadFiltersCard({ variant }: ThreadFiltersCardProps): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = ['settings', tableNameFor(variant)];

  const threadsQ = useQuery({ queryKey, queryFn: () => fetchThreads(variant) });

  const [showAdd, setShowAdd] = useState(false);
  const [channel, setChannel] = useState<ThreadChannel>('email');
  const [identifier, setIdentifier] = useState('');
  const [metaInput, setMetaInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not signed in.');
      const trimmedId = identifier.trim();
      if (!trimmedId) throw new Error('Identifier required (email address, phone, or apple-id).');
      const insertPayload: Record<string, unknown> = {
        owner_id: user.id,
        channel,
        identifier: trimmedId,
      };
      const metaTrim = metaInput.trim();
      if (metaTrim) insertPayload[metaColumnFor(variant)] = metaTrim;
      const { error: insErr } = await supabase.from(tableNameFor(variant)).insert(insertPayload);
      if (insErr) {
        const code = (insErr as { code?: string }).code;
        if (code === '23505') {
          throw new Error(`That ${channel} identifier is already on the list.`);
        }
        throw insErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setShowAdd(false);
      setIdentifier('');
      setMetaInput('');
      setChannel('email');
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: delErr } = await supabase.from(tableNameFor(variant)).delete().eq('id', id);
      if (delErr) throw delErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const rows = threadsQ.data ?? [];
  const empty = emptyStateFor(variant);

  return (
    <div className="space-y-3">
      {threadsQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {threadsQ.isError && (
        <GlassCard hover={false} className="neo-alert">
          <p className="text-sm text-alert">Failed to load: {(threadsQ.error as Error).message}</p>
        </GlassCard>
      )}

      {rows.length === 0 && !threadsQ.isLoading && (
        <GlassCard hover={false} variant="inset">
          <p className="text-sm text-foreground font-medium">{empty.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{empty.body}</p>
        </GlassCard>
      )}

      {rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 neo-inset rounded-md px-3 py-2">
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-secondary text-muted-foreground shrink-0">
                {row.channel}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-mono text-foreground truncate">{row.identifier}</p>
                {row.meta && (
                  <p className="text-[11px] text-muted-foreground truncate">{row.meta}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(row.id)}
                disabled={remove.isPending && remove.variables === row.id}
                aria-label={`Remove ${row.identifier} from ${variant} list`}
                className="text-muted-foreground hover:text-alert transition-colors p-1 rounded-md"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd ? (
        <GlassCard hover={false} variant="inset">
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2 mb-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Channel</Label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as ThreadChannel)}
                className="mt-1 w-full neo-inset rounded-md px-2 py-2 text-sm text-foreground"
              >
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="thread-identifier" className="text-[10px] uppercase tracking-wide">
                Identifier
              </Label>
              <Input
                id="thread-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={channel === 'email' ? 'name@example.com' : '+14155551234'}
                className="mt-1 font-mono text-sm"
                maxLength={200}
              />
            </div>
          </div>
          <div className="mb-3">
            <Label htmlFor="thread-meta" className="text-[10px] uppercase tracking-wide">
              {metaLabelFor(variant)}
            </Label>
            <Input
              id="thread-meta"
              value={metaInput}
              onChange={(e) => setMetaInput(e.target.value)}
              placeholder={variant === 'allowed' ? 'Friendly name' : 'Why you excluded them'}
              className="mt-1 text-sm"
              maxLength={200}
            />
          </div>
          {error && (
            <p className="text-xs text-alert mb-2" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setShowAdd(false);
                setError(null);
              }}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              {create.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Add
            </Button>
          </div>
        </GlassCard>
      ) : (
        <Button type="button" size="sm" variant="outline" onClick={() => setShowAdd(true)}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add {variant === 'allowed' ? 'allowed' : 'excluded'}
        </Button>
      )}
    </div>
  );
}
